import { Anim } from '../Animation/AnimationHelper';
import { getStackCardTargetX } from "State/Card/CardStateHelper";
import { Easing } from '@tweenjs/tween.js';
import { CARD_OFFSET_IN_STACK, CARD_STACK_OFFSET, MAX_OPENED_CARDS_IN_STACK } from "Constants/InGame";
import Renderable from 'Component/Renderable';
import TextComponent from 'Component/Render/Text';
import TransformComponent from 'Component/Transform';
import { StackCardsLabelLayout } from "Layouts/StackCardsLabelLayout";
import BaseCard from 'Prototype/Level/Card/BaseCard';
import Point from 'Utility/Point';
import CardComponent, { DEFAULT_CARD_SCALE } from 'Component/Level/Card';
import CardSystem from './CardSystem';
import { sleep } from "Utility/async";
import RelativePosition from 'Component/Render/RelativePosition';

type CardStackProps = {
    getCardsInStack: () => BaseCard[];
    system: CardSystem;
}

export class CardStack {
    private readonly getCardsInStack: () => BaseCard[];
    public readonly labelLayout: any;
    private readonly system: CardSystem;

    constructor(props: CardStackProps) {
        this.getCardsInStack = props.getCardsInStack;
        this.system = props.system;

        this.labelLayout = new StackCardsLabelLayout();
        this.labelLayout.root.getComponent(Renderable).visible = false;
        this.labelLayout.root.getComponent(TransformComponent).y = -85;
        this.labelLayout.root.addComponent(RelativePosition);
        this.labelLayout.root.getComponent(RelativePosition).y = 0;
    }

    public update() {
        this.align();
    }

    public align(): void {
        const cards = this.getCardsInStack().filter(this.filterCb);
        this.sort();

        for (let i = 0; i < cards.length; i++) {
            const card = cards[i];

            Anim(card, {
                duration: 300,
                x: getStackCardTargetX(i, cards.length),
                y: 0,
                scaleX: DEFAULT_CARD_SCALE,
                scaleY: DEFAULT_CARD_SCALE,
                angle: 0,
                easing: Easing.Sinusoidal.Out,
                delay: (cards.length - i) * 6,
            });
        }

        this.updateLabel(cards.length);
    }

    public async alignOnInsert(cardID: string, delay: number = 0): Promise<void> {
        await sleep(delay);

        const cards = this
            .getCardsInStack()
            .filter((c) => this.filterCb(c) || c.id === cardID);

        const insertIndex = cards.findIndex((c) => c.id === cardID);
        this.sort();

        for (let i = 0; i < cards.length; i++) {
            if (i === insertIndex) continue;

            const card = cards[i];
            const x = getStackCardTargetX(i, cards.length);
            const cpX = x + Math.sign(i - insertIndex) * 20;

            Anim(card, {
                duration: 500,
                x: [cpX, x],
                y: 0,
                scaleX: DEFAULT_CARD_SCALE,
                scaleY: DEFAULT_CARD_SCALE,
                angle: 0,
                easing: Easing.Sinusoidal.Out,
                delay: Math.abs(i - insertIndex) * 20,
            });
        }

        this.updateLabel(cards.length);
    }

    public alignCard(cardID: string): void {
        const cards = this
            .getCardsInStack()
            .filter((c) => this.filterCb(c) || c.id === cardID);

        const index = cards.findIndex((c) => c.id === cardID);
        const card = cards[index];

        if (!card) return;

        Anim(card, {
            duration: 500,
            x: getStackCardTargetX(index, cards.length),
            y: 0,
            scaleX: DEFAULT_CARD_SCALE,
            scaleY: DEFAULT_CARD_SCALE,
            angle: 0,
            easing: Easing.Sinusoidal.Out,
        });
    }

    public hideLabel(): void {
        Anim(this.labelLayout.root, { duration: 300, alpha: 0 });
    }

    public getCardTarget(cardID: string): Point {
        const cards = this.getCardsInStack().filter((c) => this.filterCb(c) || c.id === cardID);
        const index = cards.findIndex((c) => c.id === cardID);
        const reversedIndex = cards.length - 1 - index;
        const point = new Point();
        point.x = CARD_STACK_OFFSET + Math.min(MAX_OPENED_CARDS_IN_STACK - 1, reversedIndex) * CARD_OFFSET_IN_STACK;

        return point;
    }

    public sort(): void {
        const cards = this.getCardsInStack();

        for (const card of cards) {
            this.system.stackContainer.addChild(card);
        }
    }

    private updateLabel(stackCardsAmount: number): void {
        const amount = stackCardsAmount - MAX_OPENED_CARDS_IN_STACK;
        this.labelLayout.root.getComponent(Renderable).visible = amount > 0;
        this.labelLayout.amountText.getComponent(TextComponent).text = `${amount}`;
        this.labelLayout.root.getComponent(TransformComponent).x = getStackCardTargetX(0, stackCardsAmount) - 6;
    }

    private filterCb = (card: BaseCard): boolean => {
        return !card.getComponent(CardComponent).skipStackAlign;
    };
}
